import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { IDeviationSummary } from '../../interfaces/deviation-summary.interface';
import { SelectAllDirective } from '../../directives/select-all.directive';
import { environment } from '../../environments/environment';

@Component({
    selector: 'app-deviation-table',
    standalone: true,
    imports: [
        MatTableModule,
        MatFormFieldModule,
        MatInputModule,
        MatSortModule,
        MatPaginatorModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        SelectAllDirective, 
    ],
    templateUrl: './deviation-table.component.html',
    styleUrl: './deviation-table.component.scss', 
})
export class DeviationTableComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() tableData: IDeviationSummary[];

    tableDataSource: MatTableDataSource<IDeviationSummary> = new MatTableDataSource<IDeviationSummary>();
    swVersionFilter: string[] = [];
    adoProject = environment.adoApiConfig.project;
    displayedColumns = ["Title", "Deviation ID", "Number of Reports", "SW Version", "Total Downtime"]
    swVersion: FormControl<string[]> = new FormControl<string[]>([], { nonNullable: true });

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    ngOnInit(): void {
        this.swVersion.valueChanges.subscribe(() => {
            this.applySwFilter();
        });
        this.tableDataSource.filterPredicate = this._createFilterPredicate();
    }

    ngAfterViewInit(): void {
        this.tableDataSource.paginator = this.paginator;
        this.tableDataSource.sort = this.sort;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tableData']) { 
            this.tableDataSource.data = this.tableData;
            this.swVersionFilter = this._getUniqueSwVersions(this.tableData);
            this._setDefaultVersions();
        }
    }

    applySwFilter() {
        const selectedVersions = this.swVersion.value;
        if (!selectedVersions.length) {
            // Hack to filter out on no selection
            this.tableDataSource.filter = 'dummyfilter'; 
            return;
        }
        this.tableDataSource.filter = selectedVersions.join('|');
    }

    _updateAvailableSwVersions() {
        const uniqueSwVersions = new Set<string>();
        this.tableDataSource.data.forEach(row => {
            if (row.swFoundIn) {
                uniqueSwVersions.add(row.swFoundIn);
            }
        });
    }

    _setAllVersions() {
        this.swVersion.setValue(['select-all', ...this.swVersionFilter]);
    }

    _setDefaultVersions() {
        this._setAllVersions();
    }

    _setTableDatasource(data: IDeviationSummary[]) {
        this.tableDataSource.data = data;
        this.swVersionFilter = [];

        data.map(d => {
            this.swVersionFilter.push(d.swFoundIn ?? "Unkown");
        })
    }

    _createFilterPredicate(): (data: IDeviationSummary, filter: string) => boolean {
        return (data: IDeviationSummary, filter: string) => {
            if(!filter) {
                return false;
            }
            const filterArray = filter.split('|');
            return filterArray.some(version => data.swFoundIn.includes(version));
        };
    }

    _getUniqueSwVersions(deviations: IDeviationSummary[]) {
        const uniqueSwVersions = new Set<string>();
        deviations.forEach(dev => {
            if (dev.swFoundIn) {
                dev.swFoundIn.trim().split(",").map(ver => uniqueSwVersions.add(ver.trim()));
            }
        });
        return Array.from(uniqueSwVersions);
    }
}
